/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/ToggleButton/ToggleButton.css';
import { RadioButton } from '@adobe-commerce/elsie/components';

export interface ToggleButtonProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'icon' | 'label'> {
  label: string | VNode<HTMLAttributes<HTMLElement>>;
  name: string;
  value: string;
  busy?: boolean;
  disabled?: boolean;
  icon?:
    | VNode<HTMLAttributes<SVGSVGElement>>
    | VNode<HTMLAttributes<HTMLImageElement>>;
  onChange?: (value: string) => void;
  selected?: boolean;
}

export const ToggleButton: FunctionComponent<ToggleButtonProps> = ({
  label,
  name,
  value,
  busy = false,
  disabled = false,
  children,
  className,
  icon,
  onChange,
  selected = true,
  ...props
}) => {
  return (
    <div
      {...props}
      className={classes([
        'dropin-toggle-button',
        className,
        ['dropin-toggle-button__selected', selected],
        ['dropin-toggle-button__disabled', disabled],
      ])}
    >
      <label className="dropin-toggle-button__actionButton">
        <RadioButton
          label=""
          name={name}
          value={value}
          checked={selected}
          disabled={disabled}
          onChange={() => onChange && onChange(value)}
          aria-label={name}
          busy={busy}
          className={classes([className, 'dropin-toggle-button__radioButton'])}
        />

        <span className="dropin-toggle-button__content">
          {icon && (
            // @ts-ignore
            <icon.type
              {...icon?.props}
              className="dropin-toggle-button__icon"
            />
          )}
          {label}
        </span>
      </label>
    </div>
  );
};
